import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Fab,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Person,
  Engineering,
  Science,
  AdminPanelSettings,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import usersService, { User, UserRole, CreateUserDto, UpdateUserDto } from '../services/usersService';

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<User | null>(null);
  const [stats, setStats] = useState({
    total_users: 0,
    technicians: 0,
    analysts: 0,
    active_users: 0,
  });

  // Form state
  const [formData, setFormData] = useState<CreateUserDto>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: UserRole.TECHNICIAN_PREP,
    department: '',
    employeeId: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await usersService.getUsers(1, 100);
      setUsers(response.users);
      
      // Calculate stats
      const technicians = response.users.filter(u => u.role === UserRole.TECHNICIAN_PREP).length;
      const analysts = response.users.filter(u => u.role === UserRole.ANALYST).length;
      const activeUsers = response.users.filter(u => u.isActive).length;
      
      setStats({
        total_users: response.users.length,
        technicians,
        analysts,
        active_users: activeUsers,
      });
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      role: UserRole.TECHNICIAN_PREP,
      department: '',
      employeeId: '',
    });
    setShowUserDialog(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '', // Don't show existing password
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      department: user.department || '',
      employeeId: user.employeeId || '',
    });
    setShowUserDialog(true);
  };

  const handleSubmitUser = async () => {
    try {
      setError(null);
      
      if (editingUser) {
        // Update existing user
        const updateData: UpdateUserDto = {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          department: formData.department,
          employeeId: formData.employeeId,
        };
        
        await usersService.updateUser(editingUser.id, updateData);
        setSuccess('Utilisateur mis à jour avec succès');
      } else {
        // Create new user
        await usersService.createUser(formData);
        setSuccess('Utilisateur créé avec succès');
      }
      
      setShowUserDialog(false);
      loadUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      setError('Erreur lors de la sauvegarde de l\'utilisateur');
    }
  };

  const handleDeleteUser = async (user: User) => {
    try {
      setError(null);
      await usersService.deleteUser(user.id);
      setSuccess('Utilisateur supprimé avec succès');
      setDeleteConfirmDialog(null);
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Erreur lors de la suppression de l\'utilisateur');
    }
  };

  const getRoleIcon = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
      case UserRole.LABORATORY_MANAGER:
        return <AdminPanelSettings />;
      case UserRole.TECHNICIAN_PREP:
      case UserRole.PREPARATION_MECANIQUE:
        return <Engineering />;
      case UserRole.ANALYST:
      case UserRole.SAA_ANALYST:
      case UserRole.ICP_ANALYST:
      case UserRole.AU_ANALYST:
      case UserRole.VOLUMETRIE_ANALYST:
      case UserRole.PHYSICO_CHIMIQUE_ANALYST:
        return <Science />;
      case UserRole.SAA_PREP_CHIMIQUE:
      case UserRole.ICP_PREP_CHIMIQUE:
      case UserRole.AU_PREP_CHIMIQUE:
      case UserRole.VOLUMETRIE_PREP_CHIMIQUE:
        return <Engineering />;
      case UserRole.VALIDATION_SERVICE:
        return <AdminPanelSettings />;
      default:
        return <Person />;
    }
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return 'error';
      case UserRole.LABORATORY_MANAGER:
        return 'warning';
      case UserRole.TECHNICIAN_PREP:
      case UserRole.PREPARATION_MECANIQUE:
        return 'info';
      case UserRole.ANALYST:
      case UserRole.SAA_ANALYST:
      case UserRole.ICP_ANALYST:
      case UserRole.AU_ANALYST:
      case UserRole.VOLUMETRIE_ANALYST:
      case UserRole.PHYSICO_CHIMIQUE_ANALYST:
        return 'success';
      case UserRole.SAA_PREP_CHIMIQUE:
      case UserRole.ICP_PREP_CHIMIQUE:
      case UserRole.AU_PREP_CHIMIQUE:
      case UserRole.VOLUMETRIE_PREP_CHIMIQUE:
        return 'secondary';
      case UserRole.VALIDATION_SERVICE:
        return 'warning';
      case UserRole.QUALITY_MANAGER:
        return 'info';
      case UserRole.SAMPLE_RECEIVER:
        return 'primary';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return 'Administrateur';
      case UserRole.LABORATORY_MANAGER:
        return 'Responsable Labo';
      case UserRole.SAMPLE_RECEIVER:
        return 'Réceptionniste';
      case UserRole.TECHNICIAN_PREP:
        return 'Technicien Préparation';
      case UserRole.ANALYST:
        return 'Analyste';
      case UserRole.QUALITY_MANAGER:
        return 'Responsable Qualité';
      case UserRole.CLIENT_VIEWER:
        return 'Client';
      case UserRole.SAA_PREP_CHIMIQUE:
        return 'Préparation SAA';
      case UserRole.SAA_ANALYST:
        return 'Analyste SAA';
      case UserRole.ICP_PREP_CHIMIQUE:
        return 'Préparation ICP';
      case UserRole.ICP_ANALYST:
        return 'Analyste ICP';
      case UserRole.AU_PREP_CHIMIQUE:
        return 'Préparation AU';
      case UserRole.AU_ANALYST:
        return 'Analyste AU';
      case UserRole.VOLUMETRIE_PREP_CHIMIQUE:
        return 'Préparation Volumétrie';
      case UserRole.VOLUMETRIE_ANALYST:
        return 'Analyste Volumétrie';
      case UserRole.PHYSICO_CHIMIQUE_ANALYST:
        return 'Analyste Physico-Chimique';
      case UserRole.PREPARATION_MECANIQUE:
        return 'Préparation Mécanique';
      case UserRole.VALIDATION_SERVICE:
        return 'Service Validation';
      default:
        return role;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Gestion des Utilisateurs
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Gérer les techniciens, analystes et autres utilisateurs du système
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Person color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {stats.total_users}
                  </Typography>
                  <Typography color="text.secondary">
                    Total Utilisateurs
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Engineering color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {stats.technicians}
                  </Typography>
                  <Typography color="text.secondary">
                    Techniciens
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Science color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {stats.analysts}
                  </Typography>
                  <Typography color="text.secondary">
                    Analystes
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Person color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {stats.active_users}
                  </Typography>
                  <Typography color="text.secondary">
                    Utilisateurs Actifs
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Action Button */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<Add />}
          onClick={handleCreateUser}
          sx={{ 
            py: 2, 
            px: 4, 
            fontSize: '1.1rem',
            borderRadius: 2,
          }}
        >
          Nouvel Utilisateur
        </Button>
      </Box>

      {/* Users Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Rôle</TableCell>
                <TableCell>Département</TableCell>
                <TableCell>ID Employé</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {getRoleIcon(user.role)}
                      <Box sx={{ ml: 2 }}>
                        <Typography variant="subtitle2">
                          {user.firstName} {user.lastName}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={getRoleLabel(user.role)}
                      color={getRoleColor(user.role)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{user.department || '-'}</TableCell>
                  <TableCell>{user.employeeId || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive ? 'Actif' : 'Inactif'}
                      color={user.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Modifier">
                      <IconButton
                        size="small"
                        onClick={() => handleEditUser(user)}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    {user.id !== currentUser?.id && (
                      <Tooltip title="Supprimer">
                        <IconButton
                          size="small"
                          onClick={() => setDeleteConfirmDialog(user)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add User FAB */}
      <Fab
        color="primary"
        aria-label="add user"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleCreateUser}
      >
        <Add />
      </Fab>

      {/* User Dialog */}
      <Dialog
        open={showUserDialog}
        onClose={() => setShowUserDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingUser ? 'Modifier Utilisateur' : 'Nouvel Utilisateur'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Prénom"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Grid>
            {!editingUser && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Mot de passe"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Rôle</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                  label="Rôle"
                >
                  <MenuItem value={UserRole.TECHNICIAN_PREP}>Technicien Préparation</MenuItem>
                  <MenuItem value={UserRole.ANALYST}>Analyste</MenuItem>
                  <MenuItem value={UserRole.SAMPLE_RECEIVER}>Réceptionniste</MenuItem>
                  <MenuItem value={UserRole.QUALITY_MANAGER}>Responsable Qualité</MenuItem>
                  <MenuItem value={UserRole.LABORATORY_MANAGER}>Responsable Laboratoire</MenuItem>
                  {currentUser?.role === UserRole.ADMIN && (
                    <MenuItem value={UserRole.ADMIN}>Administrateur</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Département"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ID Employé"
                value={formData.employeeId}
                onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                placeholder="AFL-TEC-001"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUserDialog(false)}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmitUser}
            variant="contained"
            disabled={!formData.firstName || !formData.lastName || !formData.email || (!editingUser && !formData.password)}
          >
            {editingUser ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirmDialog}
        onClose={() => setDeleteConfirmDialog(null)}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer l'utilisateur{' '}
            <strong>
              {deleteConfirmDialog?.firstName} {deleteConfirmDialog?.lastName}
            </strong> ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialog(null)}>
            Annuler
          </Button>
          <Button
            onClick={() => deleteConfirmDialog && handleDeleteUser(deleteConfirmDialog)}
            color="error"
            variant="contained"
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement; 